import {
  type ConfidentialAssetActivitiesOrderBy,
  TwistedEd25519PrivateKey,
} from '@aptos-labs/confidential-asset';
import { AccountAddress } from '@aptos-labs/ts-sdk';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DollarSignIcon,
  FolderOpenIcon,
  RefreshCw,
} from 'lucide-react';
import Link from 'next/link';
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';

import { getTxExplorerUrl } from '@/api/modules/aptos';
import { confidentialAsset } from '@/api/modules/aptos/client';
import { appConfig } from '@/config';
import { trimAddress } from '@/helpers';
import { useDecryptedAmount } from '@/hooks/amount-decryption';
import { useGetAnsPrimaryName } from '@/hooks/ans';
import { cn } from '@/theme/utils';
import { UiIcon } from '@/ui';

import { useConfidentialCoinContext } from '../context';

type BaseActivity = {
  timestamp: Date;
  txnVersion: number;
  fromAddress: AccountAddress;
  toAddress: AccountAddress;
};

type DepositActivity = BaseActivity & {
  activityType: 'deposit';
  amount: number;
};

type WithdrawActivity = BaseActivity & {
  activityType: 'withdraw';
  amount: number;
};

type TransferActivity = BaseActivity & {
  activityType: 'transfer';
  /** C components of the ciphertext chunks (CompressedRistrettoPoint.data hex strings). */
  amountP: string[];
  /** D components of the ciphertext chunks for this user (CompressedRistrettoPoint.data hex strings). */
  amountR: string[];
};

type Activity = DepositActivity | WithdrawActivity | TransferActivity;

const PAGE_SIZE = 10;
const REFRESH_INTERVAL_MS = 10000;

const fetchActivities = async (
  userAddress: string,
  pageParam = 0,
): Promise<{
  activities: Activity[];
  nextCursor: number | undefined;
}> => {
  const rawActivities = await confidentialAsset.getActivities({
    where: {
      _or: [
        { owner_address: { _eq: userAddress } },
        // Also fetch transfers where this user is the recipient.
        {
          counterparty_address: { _eq: userAddress },
          event_type: { _eq: 'Transferred' },
        },
      ],
    },
    orderBy: [{ transaction_version: 'desc' } as ConfidentialAssetActivitiesOrderBy],
    offset: pageParam,
    limit: PAGE_SIZE,
  });

  const nextCursor =
    rawActivities.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined;
  const activities: Activity[] = [];

  for (const activity of rawActivities) {
    const base = {
      timestamp: new Date(activity.transaction_timestamp + 'Z'),
      txnVersion: Number(activity.transaction_version),
    };

    if (activity.event_type === 'Deposited') {
      activities.push({
        activityType: 'deposit',
        fromAddress: AccountAddress.from(activity.owner_address),
        toAddress: AccountAddress.from(activity.owner_address),
        amount: Number(activity.amount),
        ...base,
      });
    } else if (activity.event_type === 'Withdrawn') {
      activities.push({
        activityType: 'withdraw',
        fromAddress: AccountAddress.from(activity.owner_address),
        toAddress: activity.counterparty_address
          ? AccountAddress.from(activity.counterparty_address)
          : AccountAddress.from(activity.owner_address),
        amount: Number(activity.amount),
        ...base,
      });
    } else if (activity.event_type === 'Transferred') {
      // owner_address is always the sender; counterparty_address is the recipient.
      const isOutgoing =
        activity.owner_address.toLowerCase() === userAddress.toLowerCase();
      activities.push({
        activityType: 'transfer',
        fromAddress: AccountAddress.from(activity.owner_address),
        toAddress: AccountAddress.from(activity.counterparty_address),
        amountP: activity.event_data.amount_P.map(p =>
          p.data.startsWith('0x') ? p.data.slice(2) : p.data,
        ),
        amountR: isOutgoing
          ? activity.event_data.amount_R_sender.map(r =>
              r.data.startsWith('0x') ? r.data.slice(2) : r.data,
            )
          : activity.event_data.amount_R_recip.map(r =>
              r.data.startsWith('0x') ? r.data.slice(2) : r.data,
            ),
        ...base,
      });
    }
    // Skip Registered, Normalized, RolledOver, KeyRotated, etc.
  }

  return { activities, nextCursor };
};

export default function ActivitiesFeed() {
  // TODO: Instead of using selected token here we should use the token from the
  // activity, if we support multiple assets down the line.
  const { selectedAccount, selectedToken, reloadBalances } =
    useConfidentialCoinContext();

  // Reference to the load more trigger element
  const observerTarget = useRef<HTMLDivElement>(null);

  // Keep track of the latest activity to detect changes.
  const [latestIncomingActivity, setLatestIncomingActivity] = useState<{
    txnVersion: number;
    timestamp: Date;
  } | null>(null);

  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_MS / 1000);

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
    dataUpdatedAt,
  } = useInfiniteQuery({
    queryKey: ['activities', selectedAccount.accountAddress.toString()],
    queryFn: async ({ pageParam = 0 }) => {
      const out = await fetchActivities(
        selectedAccount.accountAddress.toString(),
        pageParam,
      );
      conditionallyReloadBalances(out.activities, pageParam);
      return out;
    },
    getNextPageParam: lastPage => lastPage.nextCursor,
    initialPageParam: 0,
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
  });

  /**
   * Call this function to conditionally reload the balance. The reload will occur if
   * there are new activites that we didn't initiate (e.g. receiving funds).
   */
  function conditionallyReloadBalances(activities: Activity[], pageParam: number) {
    if (activities.length === 0 || pageParam !== 0) {
      return;
    }

    // Sort activities by timestamp (newest first)
    const sortedActivities = [...activities].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    );
    const newestActivity = sortedActivities[0];

    const currentAccountAddress = selectedAccount.accountAddress.toString();

    // Find if there are any new incoming transactions among all activities
    let newIncomingTransactionPresent = false;
    for (const activity of sortedActivities) {
      if (
        activity.activityType === 'transfer' &&
        activity.fromAddress.toString() !== currentAccountAddress &&
        (latestIncomingActivity === null ||
          activity.timestamp.getTime() > latestIncomingActivity.timestamp.getTime())
      ) {
        newIncomingTransactionPresent = true;
        break;
      }
    }

    // Only reload balances if we found a new incoming transaction.
    if (newIncomingTransactionPresent) {
      setLatestIncomingActivity({
        txnVersion: newestActivity.txnVersion,
        timestamp: newestActivity.timestamp,
      });
      reloadBalances();
    }
  }

  // Add countdown timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const updateCountdown = () => {
      setCountdown(prev => {
        if (prev <= 0) return REFRESH_INTERVAL_MS / 1000;
        return prev - 1;
      });
    };

    // Reset countdown when data is fetched or refetching starts
    setCountdown(REFRESH_INTERVAL_MS / 1000);
    // eslint-disable-next-line prefer-const
    timer = setInterval(updateCountdown, 1000);

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isRefetching, dataUpdatedAt]); // Add dataUpdatedAt to dependencies

  // Flatten the activities from all pages
  const allActivities = data?.pages.flatMap(page => page.activities) || [];

  // Implement intersection observer for infinite scrolling
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  return (
    <div
      className={cn(
        'mt-8 flex w-full flex-1 flex-col p-4',
        'md:mx-auto md:max-w-[500px]',
      )}
    >
      <div className='mb-3 flex items-center justify-between'>
        <h2 className='typography-h5 text-textPrimary'>Activity</h2>
        <div className='flex items-center gap-2'>
          <span className='typography-caption1 text-textSecondary'>{countdown}s</span>
          <button
            onClick={() => refetch()}
            className={cn(
              'hover:bg-componentHover flex items-center justify-center gap-2 rounded-md py-1.5',
              isRefetching && 'opacity-60',
            )}
            disabled={isRefetching}
            aria-label='Refresh activities'
          >
            <RefreshCw
              size={16}
              className={cn('text-textSecondary', isRefetching && 'animate-spin')}
            />
            {isRefetching ? (
              <span className='typography-body3 text-textSecondary'>Loading...</span>
            ) : (
              <span className='typography-body3 text-textSecondary'>Refresh</span>
            )}
          </button>
        </div>
      </div>

      {isError && (
        <div className='mb-3 rounded-md bg-red-100 p-3 text-red-800'>
          <p>
            Failed to load activities.{' '}
            {error instanceof Error ? error.message : 'Unexpected error.'}
          </p>
        </div>
      )}

      {isLoading && allActivities.length === 0 ? (
        <div className='my-6 flex justify-center'>
          <div className='animate-spin'>
            <RefreshCw size={32} className='text-textSecondary' />
          </div>
        </div>
      ) : allActivities.length ? (
        <div className='relative flex flex-col gap-6'>
          {allActivities
            .sort((a, b) => {
              return b.timestamp.getTime() - a.timestamp.getTime();
            })
            .map((activity, idx) => (
              <TxItem
                key={idx}
                {...activity}
                currentAddress={selectedAccount.accountAddress}
                tokenSymbol={selectedToken.symbol}
                tokenDecimals={selectedToken.decimals}
              />
            ))}

          {/* Load more trigger element */}
          <div ref={observerTarget} className='flex justify-center py-2'>
            {isFetchingNextPage ? (
              <div className='animate-spin'>
                <RefreshCw size={18} className='text-textSecondary' />
              </div>
            ) : hasNextPage ? (
              <span className='typography-body4 text-textSecondary'>
                Scroll to load more
              </span>
            ) : (
              <span className='typography-body4 text-textSecondary'>
                No more activities
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className='flex w-full flex-col items-center gap-4 self-center'>
          <TxEmptyComponent />
        </div>
      )}
    </div>
  );
}

function TxItem({
  activityType,
  timestamp,
  txnVersion,
  fromAddress,
  toAddress,
  currentAddress,
  tokenSymbol,
  tokenDecimals,
  amount: amountRaw,
  amountP,
  amountR,
}: Activity & {
  currentAddress: AccountAddress;
  tokenSymbol: string;
  tokenDecimals: number;
  amount?: number;
  amountP?: string[];
  amountR?: string[];
}) {
  const { selectedAccountDecryptionKey } = useConfidentialCoinContext();

  const isOutgoing = fromAddress.toString() === currentAddress.toString();

  // This case can only happen on testnet, courtesy of minting.
  const isSelfDeposit =
    activityType === 'deposit' && fromAddress.toString() === currentAddress.toString();

  let title = '';
  let icon;

  if (activityType === 'transfer') {
    title = isOutgoing ? 'Sent' : 'Received';
    icon = isOutgoing ? (
      <ArrowUpIcon size={18} className='text-textPrimary' />
    ) : (
      <ArrowDownIcon size={18} className='text-textPrimary' />
    );
  } else if (activityType === 'deposit') {
    title = 'Deposit';
    icon = isSelfDeposit ? (
      <DollarSignIcon size={18} className='text-textPrimary' />
    ) : (
      <ArrowDownIcon size={18} className='text-textPrimary' />
    );
  } else if (activityType === 'withdraw') {
    title = 'Withdraw';
    icon = <ArrowUpIcon size={18} className='text-textPrimary' />;
  }

  const counterpartyAddress =
    activityType === 'transfer'
      ? isOutgoing
        ? toAddress
        : fromAddress
      : activityType === 'deposit'
        ? fromAddress
        : toAddress;

  const directionLabel =
    (activityType === 'transfer' && (isOutgoing ? 'To: ' : 'From: ')) ||
    (activityType === 'deposit' && 'From: ') ||
    (activityType === 'withdraw' && 'To: ') ||
    '';

  return (
    <div className='flex flex-col'>
      <div className='flex flex-row items-center gap-3 py-3'>
        <div
          className={cn(
            'flex size-[36px] flex-col items-center justify-center rounded-full',
            'bg-componentSelected',
          )}
        >
          {icon}
        </div>

        <div className='flex flex-1 flex-col gap-1.5'>
          <div className='flex items-center justify-between'>
            <span className='typography-subtitle3 text-textPrimary'>{title}</span>
            {amountP && amountR ? (
              <EncryptedAmountDisplay
                amountP={amountP}
                amountR={amountR}
                decryptionKey={selectedAccountDecryptionKey}
                tokenSymbol={tokenSymbol}
                tokenDecimals={tokenDecimals}
              />
            ) : amountRaw !== undefined ? (
              <PlainAmountDisplay
                amount={amountRaw}
                tokenSymbol={tokenSymbol}
                tokenDecimals={tokenDecimals}
              />
            ) : null}
          </div>

          {/* To/From information */}
          <div className='flex items-center gap-1'>
            <span className='typography-caption1 text-textSecondary'>
              {directionLabel}
            </span>
            <>
              <AddressDisplay address={counterpartyAddress} />
              <div className='ml-2' />
            </>

            <Link
              href={getTxExplorerUrl(txnVersion.toString())}
              target='_blank'
              className='ml-auto'
              aria-label='View transaction details'
            >
              <div className='flex items-center gap-1'>
                <UiIcon name='CalendarIcon' size={12} className='text-textSecondary' />
                {timestamp && (
                  <span className='typography-caption2 text-textSecondary'>
                    {timestamp.toLocaleString()}
                  </span>
                )}
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Component for displaying plain (unencrypted) amounts
function PlainAmountDisplay({
  amount,
  tokenSymbol,
  tokenDecimals,
}: {
  amount: number;
  tokenSymbol: string;
  tokenDecimals: number;
}) {
  return (
    <span className='typography-body3 text-textPrimary'>
      {formatAmount(amount, tokenSymbol, tokenDecimals)}
    </span>
  );
}

// Component for displaying encrypted amounts with decryption handling
function EncryptedAmountDisplay({
  amountP,
  amountR,
  decryptionKey,
  tokenSymbol,
  tokenDecimals,
}: {
  amountP: string[];
  amountR: string[];
  decryptionKey: TwistedEd25519PrivateKey;
  tokenSymbol: string;
  tokenDecimals: number;
}) {
  const {
    amount: decryptedAmount,
    isLoading: isDecrypting,
    error: decryptionError,
  } = useDecryptedAmount(amountP, amountR, decryptionKey);

  if (isDecrypting) {
    return (
      <span className='typography-body3 text-textPrimary'>
        <RefreshCw size={12} className='animate-spin' />
      </span>
    );
  }

  if (decryptionError || decryptedAmount === undefined) {
    return <span className='typography-body3 text-red-500'>Decryption failed</span>;
  }

  return (
    <span className='typography-body3 text-textPrimary'>
      {formatAmount(decryptedAmount, tokenSymbol, tokenDecimals)}
    </span>
  );
}

function TxEmptyComponent({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn('relative isolate flex w-full flex-col gap-4', className)}
    >
      {Array(5)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex flex-row items-center gap-4 py-3 opacity-20',
              className,
            )}
          >
            <div className='size-[48px] rounded-full bg-muted' />
            <div className='flex flex-1 flex-col gap-2'>
              <div className='h-4 w-[120px] bg-muted' />
              <div className='h-4 w-[80px] bg-muted' />
            </div>
            <div className='size-6 rounded-full bg-muted' />
          </div>
        ))}

      <div className='absolute-center flex flex-col items-center'>
        <FolderOpenIcon size={128} className='text-componentDisabled' />

        <span className='typography-subtitle2 text-textSecondary'>
          No transactions yet.
        </span>
      </div>
    </div>
  );
}

// Formats addresses for display - either showing ANS name or shortened address
const AddressDisplay = ({ address }: { address: AccountAddress }) => {
  const { data: name, isLoading } = useGetAnsPrimaryName({
    accountAddress: address,
  });

  if (isLoading) {
    return (
      <span className='typography-caption1 flex items-center text-textSecondary'>
        <RefreshCw size={12} className='mr-1 animate-spin' />
      </span>
    );
  }

  if (name && name.includes(appConfig.ANS_DOMAIN)) {
    // Extract the part before the first dot if it's a valid name
    const formattedName = name.split('.')[0];
    return (
      <span className='typography-caption1 text-textPrimary'>@{formattedName}</span>
    );
  }

  // Shorten address for display (0x123...789)
  const shortAddress = trimAddress(address.toString());

  return <span className='typography-caption1 text-textPrimary'>{shortAddress}</span>;
};

// Format amount with token symbol
const formatAmount = (amount: number, symbol: string, decimals: number) => {
  const properAmount = amount / 10 ** decimals;
  return `${properAmount.toLocaleString()} ${symbol}`;
};
